/**
 * Which internal links are worth a speculative server render, and which are not.
 *
 * WHY THIS IS A MODULE AND NOT A PROP AT EACH CALL SITE. Next dedupes prefetch BY HREF, so a path
 * linked from four components only stops prefetching when ALL FOUR are fixed. Annotating call sites
 * one at a time produced ZERO measurable reduction twice: the sidebar and footer were fixed while
 * the homepage still rendered the same paths from SEO grids and hero sections, and the prefetch
 * fired anyway. Every round of hunting found more sources, several with DYNAMIC hrefs that no
 * literal-string search can see. A single policy keyed on the href is the only complete answer.
 *
 * Register: rsc-prefetch-is-a-seventh-of-the-box-1008.
 *
 * THE MEASUREMENT, 2026-10-01, one full day of nginx, RSC payload requests against real document
 * loads for the same path. A prefetch that is never followed is pure server cost:
 *     /chart              15,754 : 1      (31,507 payloads,      2 documents)
 *     /watchlist           3,755 : 1
 *     /donate              2,523 : 1
 *     /site-guide          1,692 : 1
 *     /guides              1,359 : 1
 *     /radar               1,343 : 1
 *     /alerts                715 : 1
 *     /stock-scanners        667 : 1
 *     /stock-market-today    438 : 1
 *     /top-stocks            177 : 1
 *     /rvol-scanner          112 : 1
 *
 * ⛔ DELIBERATELY ABSENT, and it is the positive control when verifying this: `/` is genuinely
 * followed, so prefetch earns its cost there. If a verification shows IT disappearing too then the
 * rule over-matched and should be treated as broken. Across three days: 5.5 : 1 (10-06), 14.1 : 1
 * (10-09), 53 : 1 (10-01).
 *
 * ⛔⛔ `/screener` USED TO BE THE SECOND CONTROL AND THAT WAS A MISTAKE, corrected 2026-10-10. I read
 * its ratio as 5.5 : 1 from day 2026-10-01 (30,967 payloads / 5,631 documents) and excluded it. But
 * 10-01 is the BOT SPIKE DAY: it carried 1.05M browser-UA document requests against a 130k-300k norm,
 * so that 5,631 was inflated. Three normal days disagree flatly:
 *     10-06    7,096 payloads /  87 documents  =  81.6 : 1
 *     10-08   33,988          /  69            = 492.6 : 1
 *     10-09   15,335          /  70            = 219.1 : 1
 * ⚠ So a "control" chosen from a single day's ratio was calibrated on the one anomalous day in the
 * window, and the honest reading is that /screener belongs in the list below. ⭐ The mechanical check
 * it performed was still valid (the rule did only what it was coded to do); the CHOICE was wrong.
 * ⛔ Never pick a baseline day without checking it against its neighbours first.
 *
 * ⚠ This list is measurements, not taste. Do not add a path because it feels unimportant - re-run
 * scripts/analysis/rsc-by-shape.sh and add it because the ratio says so.
 *
 * ⚠ THE SIDEBAR USES A STRICTER RULE AND THAT IS NOT A DUPLICATE OF THIS. Navbar.tsx carries
 * PREFETCH_EARNS_ITS_KEEP, which disables prefetch for ALL 91 SIDEBAR_GROUPS items except `/` and
 * `/screener`. The justification there is structural rather than per-path: a menu is a menu, a
 * visitor picks ONE item, so pre-rendering every visible entry is waste by construction and no
 * per-item ratio is needed. This module is the narrower, measurement-only rule for links that
 * appear OUTSIDE the menu - prose, SEO grids, hero sections. The two agree on all 11 paths below.
 */
const PREFETCH_NOT_WORTH_IT: ReadonlySet<string> = new Set([
  '/chart',
  '/watchlist',
  '/donate',
  '/site-guide',
  '/guides',
  '/radar',
  '/alerts',
  '/stock-scanners',
  '/stock-market-today',
  '/top-stocks',
  '/rvol-scanner',
  // Added 2026-10-10 from the first clean post-fix day (2026-10-09), where these rose to the top of
  // the table once the eleven above dropped out of it. All measured, none added on taste:
  '/terms',          // 1,948 : 1   (9,741 payloads,  5 documents)
  '/privacy-policy', // 1,390 : 1   (9,728,           7)
  '/track-record',   //   443 : 1   (10,177,         23)
  '/screener',       //    75-493 : 1 across four normal days. See the correction note above.
])

/**
 * The value to pass to `<Link prefetch={...}>`.
 *
 * Returns `false` to suppress the speculative render, or `undefined` to leave Next's default alone.
 * ⭐ `undefined` is important rather than `true`: passing `true` would FORCE a full prefetch and make
 * the untouched links MORE expensive than they are today.
 *
 * ⚠ Matches the path EXACTLY, after stripping any query or hash. So `/alerts` does not swallow
 * `/alerts/price`, and `/chart` does not swallow `/chart/SPY` - those are separate routes with
 * separate ratios (/chart/<SYM> measured 24.6 : 1 and is handled where it is rendered).
 */
export function prefetchFor(href: string): false | undefined {
  if (!href || href.charAt(0) !== '/') return undefined
  const path = href.split('?')[0].split('#')[0]
  return PREFETCH_NOT_WORTH_IT.has(path) ? false : undefined
}

/**
 * The one sidebar destination that keeps its prefetch. Everything else in the menu loses it.
 *
 * ⭐ THE MENU RULE IS STRUCTURAL, NOT PER-PATH, which is why it is stricter than prefetchFor():
 * SIDEBAR_GROUPS holds 91 items and a visitor picks ONE, so speculatively rendering every entry in
 * the viewport is waste by construction and needs no per-item ratio. `/` is the measured exception,
 * genuinely followed at 5.1 to 14.1 : 1 across normal days.
 *
 * ⛔ `/screener` WAS HERE UNTIL 2026-10-10 and should not be put back. See the correction note at the
 * top of this file: its 5.5 : 1 came from the 10-01 bot-spike day, and four normal days read 75 to
 * 493 : 1.
 */
const NAV_PREFETCH_EARNS_ITS_KEEP: ReadonlySet<string> = new Set(['/'])

/**
 * The value to pass to `<Link prefetch={...}>` for a SIDEBAR nav item.
 *
 * ⚠ PASS THE ITEM'S CANONICAL href, not the computed one. navHref() rewrites `/profile` into
 * `/profile/<username>`, so handing it the computed value would never match the set.
 *
 * 🚨 THE SIDEBAR IS RENDERED BY TWO COMPONENTS AND BOTH MUST USE THIS. Navbar.tsx draws the desktop
 * rail and the mobile drawer; WatchlistPane.tsx draws the one that is actually on screen for a
 * logged-out visitor. Fixing only Navbar measured EXACTLY ZERO in production on 2026-10-08, because
 * Next dedupes prefetch by href and WatchlistPane kept requesting the same paths. ⛔ If a third
 * component ever renders sidebarGroupsFor(), it needs this too or the whole thing silently reverts.
 */
export function prefetchForNavItem(canonicalHref: string): false | undefined {
  return NAV_PREFETCH_EARNS_ITS_KEEP.has(canonicalHref) ? undefined : false
}
